import { type JSX, useCallback, useEffect, useRef } from 'react';

const STORAGE_KEY = 'mobilauncher.builder.previewWidth.v1';
const SSR_FALLBACK = 720;
const MIN_WIDTH = 280;

/**
 * First-time default: ~55% of the viewport, clamped to a band that keeps
 * the device bezel readable on big monitors and the editor usable on
 * small laptops. Replaces the original fixed 420px which was too narrow
 * to show portrait handheld bezels comfortably.
 */
function viewportDefault(): number {
  if (typeof window === 'undefined') return SSR_FALLBACK;
  return Math.min(1100, Math.max(480, Math.round(window.innerWidth * 0.55)));
}

/**
 * Read the persisted preview-pane width, clamped to a reasonable floor so
 * a stuck-zero value can't lock the user out of the preview.
 */
export function loadPreviewWidth(): number {
  if (typeof window === 'undefined') return SSR_FALLBACK;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return viewportDefault();
    const n = Number(raw);
    if (!Number.isFinite(n) || n < MIN_WIDTH) return viewportDefault();
    return n;
  } catch {
    return viewportDefault();
  }
}

export interface SplitterProps {
  /** Current preview-pane width in CSS px. */
  width: number;
  /** Called continuously during drag with the new px width. */
  onChange: (next: number) => void;
}

/**
 * Vertical 4-px-wide drag handle between the editor and preview columns.
 *
 * Drag math: the preview pane sits on the right edge of the body grid, so
 * the new width is `viewport.right - pointer.clientX`. We clamp against a
 * floor (so the pane never disappears) and a ceiling derived from the
 * viewport (so the editor never collapses to zero either). Pointer
 * Capture means we keep getting move events even if the pointer drifts
 * outside the handle's hitbox mid-drag.
 *
 * Why not CSS resize: `resize: horizontal` works only on the element
 * itself (would need to live ON the preview, with the wrong handle
 * affordance and no min/max clamping).
 */
export function Splitter({ width, onChange }: SplitterProps): JSX.Element {
  const draggingRef = useRef(false);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.classList.add('builder--resizing');
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      const viewportW = window.innerWidth;
      // Right-aligned column → new width = distance from cursor to right edge.
      const next = viewportW - e.clientX;
      // Leave at least 320px for the editor middle column, and clamp the
      // floor so the splitter handle stays grabbable.
      const max = Math.max(MIN_WIDTH, viewportW - 320);
      const clamped = Math.min(Math.max(next, MIN_WIDTH), max);
      onChange(clamped);
    },
    [onChange],
  );

  const stopDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // releasePointerCapture throws if the pointer was never captured —
      // safe to ignore.
    }
    document.body.classList.remove('builder--resizing');
  }, []);

  // Safety net: if the body class somehow lingers (component unmounts
  // mid-drag, hot reload, etc.) drop it on unmount.
  useEffect(
    () => () => {
      document.body.classList.remove('builder--resizing');
    },
    [],
  );

  return (
    <div
      className="builder__splitter"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize preview pane"
      aria-valuenow={Math.round(width)}
      aria-valuemin={MIN_WIDTH}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
      onKeyDown={(e) => {
        // Keyboard adjust — 24px nudge, 96px with shift.
        const step = e.shiftKey ? 96 : 24;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          onChange(Math.max(MIN_WIDTH, width + step));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          onChange(Math.max(MIN_WIDTH, width - step));
        }
      }}
      data-testid="builder-splitter"
    >
      <span className="builder__splitter-grip" aria-hidden />
    </div>
  );
}

/** Persist a width — debounce is overkill at this scale, just write directly. */
export function savePreviewWidth(width: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(Math.round(width)));
  } catch {
    // ignored — quota / private-mode
  }
}
